import React, { ReactNode } from "react";
import {
  Routes,
  Route,
  Navigate,
  NavigateFunction,
  Params,
} from "react-router-dom";
import { Provider, inject, observer } from "mobx-react";
import ProductProtectedRoute from "./ProductProtectedRoute";
import { Subject, takeUntil } from "rxjs";
import { ProductPricingPage, ProductVariantPage } from "./components";
import ProductStore from "./ProductStore";
import {
  Header,
  ItemDetailPage,
  ItemHeader,
  Layout,
  SideBar,
} from "../../shared/components";
import {
  Loader,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  AuthService,
  ProductService,
  RelatedItemsService,
} from "../../shared/services";
import { UpsertProductActiveStep } from "../../shared/enums";
import { ProductDetailWrapper } from "../../shared/styles";
import { ItemDetailInputModel } from "../../shared/models";
import RelatedItemsSection from "../../shared/components/RelatedItemsSection";
import { RolesAndPermissionStore } from "../../shared/store";
import CategoryServiceStore from "../stores/CategoryServiceStore";

interface IProps {
  productStore?: ProductStore;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  rolesAndPermissionStore: RolesAndPermissionStore;
}

@inject("productStore")
@observer
class ProductRoutes extends React.Component<IProps> {
  private loader: Loader = new Loader(false);
  private $destroy: Subject<boolean> = new Subject<boolean>();
  onCancel() {
    const { activeStep } = this.props.productStore || {};
    if (
      activeStep == UpsertProductActiveStep.Pricing ||
      activeStep == UpsertProductActiveStep.Variants
    ) {
      this.onBackOrNext(false);
      return;
    }
    this.props.navigate(AuthService.prefixRoute + "items");
  }

  get productId() {
    const split = ((this.props.params && this.props.params["*"]) || "").split(
      "/"
    );
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return undefined;
  }

  setDefaultCountry = () => {
    if (!this.props.productStore) return;
    if (this.props.productStore.countryStore?.countriesWithState.length === 0) {
      const subscription =
        this.props.productStore.countryStore.onCountryDataLoaded.subscribe(
          () => {
            subscription.unsubscribe();
            this.setDefaultCountry();
          }
        );
      return;
    }
    const currentCountryId =
      this.props.productStore.detailPage?.selectedCountry?.id;
    const selectedCountry = currentCountryId
      ? this.props.productStore.countryStore?.countriesWithState.find(
          (c) => c.id === currentCountryId
        )
      : this.props.productStore.countryStore?.getUSCountry();

    if (selectedCountry) {
      this.props.productStore?.updateDetailPage(
        new ItemDetailInputModel({
          ...this.props.productStore.detailPage,
          selectedCountry,
        })
      );
    }
  };

  componentDidMount(): void {
    const { productStore } = this.props;
    productStore?.reset();
    if (this.productId) {
      this.loader.show();
      ProductService.getProduct(AuthService.authData.accountId, this.productId)
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (result) => {
            if (result.result) {
              productStore?.constructStoreFromAPI(result.result);
              this.setDefaultCountry();
            } else {
              ToastService.showError("Error in loading product details");
            }
          },
          error: () => {
            ToastService.showError("Error in loading product details");
          },
          complete: () => {
            this.loader.hide();
          },
        });
    } else {
      this.setDefaultCountry();
    }
  }

  componentWillUnmount(): void {
    this.$destroy.next(true);
  }

  onBackOrNext(isNext: boolean) {
    if (this.loader.showLoader) return;
    let queryParam: string = "";
    if (this.productId) {
      queryParam = "/" + this.productId;
    }
    if (isNext) {
      const success = this.props.productStore?.validate();
      if (!success) return;
    }
    switch (this.props.productStore?.activeStep) {
      case UpsertProductActiveStep.Detail:
        this.props.productStore?.updateActiveStep(
          UpsertProductActiveStep.Pricing
        );
        this.props.navigate(
          AuthService.prefixRoute + `product/pricing${queryParam}`
        );
        break;
      case UpsertProductActiveStep.Pricing:
        if (this.props.productStore?.detailPage.hasVariants) {
          this.props.productStore?.updateActiveStep(
            isNext
              ? UpsertProductActiveStep.Variants
              : UpsertProductActiveStep.Detail
          );
          isNext
            ? this.props.navigate(
                AuthService.prefixRoute + `product/variants${queryParam}`
              )
            : this.props.navigate(
                AuthService.prefixRoute + `product/detail${queryParam}`
              );
        } else {
          this.props.productStore?.updateActiveStep(
            isNext
              ? UpsertProductActiveStep.Options
              : UpsertProductActiveStep.Detail
          );
          isNext
            ? this.props.navigate(
                AuthService.prefixRoute + `product/options${queryParam}`
              )
            : this.props.navigate(
                AuthService.prefixRoute + `product/detail${queryParam}`
              );
        }
        break;
      case UpsertProductActiveStep.Variants:
        this.props.productStore?.updateActiveStep(
          isNext
            ? UpsertProductActiveStep.Options
            : UpsertProductActiveStep.Pricing
        );
        isNext
          ? this.props.navigate(
              AuthService.prefixRoute + `product/options${queryParam}`
            )
          : this.props.navigate(
              AuthService.prefixRoute + `product/pricing${queryParam}`
            );
        break;
      case UpsertProductActiveStep.Options:
        if (this.props.productStore?.detailPage.hasVariants) {
          this.props.productStore?.updateActiveStep(
            UpsertProductActiveStep.Variants
          );
          this.props.navigate(
            AuthService.prefixRoute + `product/variants${queryParam}`
          );
        } else {
          this.props.productStore?.updateActiveStep(
            UpsertProductActiveStep.Pricing
          );
          this.props.navigate(
            AuthService.prefixRoute + `product/pricing${queryParam}`
          );
        }

        break;
    }
  }

  private stepConfig = {
    [UpsertProductActiveStep.Detail]: {
      name: "details",
      url: AuthService.prefixRoute + "product/detail",
    },
    [UpsertProductActiveStep.Pricing]: {
      name: "pricing",
      url: AuthService.prefixRoute + "product/pricing",
    },
    [UpsertProductActiveStep.Variants]: {
      name: "variants",
      url: AuthService.prefixRoute + "product/variants",
    },
    [UpsertProductActiveStep.Options]: {
      name: "options",
      url: AuthService.prefixRoute + "product/options",
    },
  };

  onTabChange = (key: string) => {
    const step = key as UpsertProductActiveStep;
    const queryParam = this.productId || "";

    const validationResult =
      this.props.productStore?.validatePreviousSteps(step);
    if (!validationResult) {
      ToastService.showError("Unable to validate steps.");
      return;
    }

    const { isValid, errorStep } = validationResult;
    if (!isValid && errorStep) {
      ToastService.showError(
        `Please fill all the required fields in product ${this.stepConfig[errorStep].name} before proceeding.`
      );
      this.props.productStore?.updateActiveStep(errorStep);
      this.props.navigate(`${this.stepConfig[errorStep].url}/${queryParam}`);
      return;
    }

    this.props.productStore?.updateActiveStep(step);
    this.props.navigate(`${this.stepConfig[step].url}/${queryParam}`);
  };

  onSave(isSaveAndExit: boolean = false) {
    if (this.loader.showLoader) return;

    if (isSaveAndExit) {
      const validationResult = this.props.productStore?.validatePreviousSteps(
        UpsertProductActiveStep.Options
      );

      if (!validationResult) {
        ToastService.showError("Validation failed.");
        return;
      }

      const { isValid, errorStep } = validationResult;
      if (!isValid && errorStep) {
        ToastService.showError(
          `Please fill all the required fields in product ${this.stepConfig[errorStep].name} before saving.`
        );
        this.props.productStore?.updateActiveStep(errorStep);
        this.props.navigate(
          `${this.stepConfig[errorStep].url}/${this.productId || ""}`
        );
        return;
      }

      const isOptionsValid = this.props.productStore?.validate(
        UpsertProductActiveStep.Options
      );
      if (!isOptionsValid) {
        ToastService.showError(
          "Please fill all required fields in the Options step before saving."
        );
        this.props.productStore?.updateActiveStep(
          UpsertProductActiveStep.Options
        );
        this.props.navigate(
          `${this.stepConfig[UpsertProductActiveStep.Options].url}/${this.productId || ""}`
        );
        return;
      }
    } else {
      const success = this.props.productStore?.validate();
      if (!success) return;
    }

    if (this.props.productStore?.isSaveUpdateInProgress) return;
    this.props.productStore?.toggleSaveUpdateInProgress();

    if (!this.props.productStore) return;
    const request = this.props.productStore.createSaveModel();

    if (this.productId) {
      request.id = this.productId;
      ProductService.updateProduct(request).subscribe({
        next: (response) => {
          if (response) {
            ToastService.showSuccess("Product updated Successfully");
            this.props.productStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate(AuthService.prefixRoute + "items");
            }
          }
        },
        error: () => {
          ToastService.showError("Product update failed!");
          this.props.productStore?.toggleSaveUpdateInProgress();
        },
      });
    } else {
      ProductService.saveProduct(request).subscribe({
        next: (response) => {
          if (response?.id) {
            ToastService.showSuccess("Product Created Successfully");
            this.props.productStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate(AuthService.prefixRoute + "items");
            }
          }
        },
        error: () => {
          ToastService.showError("Product save failed!");
          this.props.productStore?.toggleSaveUpdateInProgress();
        },
      });
    }
  }

  get showNextButton(): boolean {
    return (
      this.props.productStore?.activeStep !== UpsertProductActiveStep.Options
    );
  }

  get showBackbutton(): boolean {
    return (
      this.props.productStore?.activeStep !== UpsertProductActiveStep.Detail
    );
  }

  get headerCancelButtonCaption(): string {
    return this.props.productStore?.activeStep ===
      UpsertProductActiveStep.Pricing ||
      this.props.productStore?.activeStep == UpsertProductActiveStep.Variants
      ? "Back"
      : "";
  }

  get routes() {
    return (
      <Routes>
        <Route
          path={`/detail/:id?`}
          element={
            <ProductProtectedRoute currentRoute="detail">
              <ProductDetailWrapper>
                <ItemHeader header="Product Details" />
                <ItemDetailPage
                  isProduct={true}
                  itemDetail={
                    this.props.productStore?.detailPage ||
                    new ItemDetailInputModel()
                  }
                  onUpdate={(detail: ItemDetailInputModel) =>
                    this.props.productStore?.updateDetailPage(detail)
                  }
                  countriesWithState={
                    this.props.productStore?.countriesWithState || []
                  }
                />
              </ProductDetailWrapper>
            </ProductProtectedRoute>
          }
        />
        <Route
          path={`/pricing/:id?`}
          element={
            <ProductProtectedRoute currentRoute="pricing">
              <ItemHeader header="Product Pricing" />
              <ProductPricingPage />
            </ProductProtectedRoute>
          }
        />
        <Route
          path={`/variants/:id?`}
          element={
            <ProductProtectedRoute currentRoute="variants">
              <ItemHeader header="Product Variants" />
              <ProductVariantPage />
            </ProductProtectedRoute>
          }
        />
        <Route
          path={`/options/:id?`}
          element={
            <ProductProtectedRoute currentRoute="options">
              <ItemHeader header="Product Options" />
              <RelatedItemsSection
                sourceId={this.productId}
                realtedItemService={
                  this.props.productStore?.realtedItemService ||
                  new RelatedItemsService()
                }
              />
            </ProductProtectedRoute>
          }
        />
        <Route
          path="*"
          element={
            <Navigate to={AuthService.prefixRoute + "product/detail/"} />
          }
        />
      </Routes>
    );
  }

  render(): ReactNode {
    return (
      <Provider {...CategoryServiceStore}>
        <Layout
          sideBarComponent={
            <SideBar
              activeStep={this.props.productStore?.activeStep?.toString() || ""}
              showVariant={
                this.props.productStore?.detailPage.hasVariants || false
              }
              onTabChange={(key: UpsertProductActiveStep) =>
                this.onTabChange(key)
              }
            />
          }
          saveAndExitClick={() => this.onSave(true)}
          showSaveAndExit={this.productId ? true : false}
          headerCaption={this.props.productStore?.headerCaption || ""}
          cancelClick={() => this.onCancel()}
          nextButtonCaption={this.showNextButton ? "Next" : ""}
          nextClick={() => this.onBackOrNext(true)}
          showBackButton={this.showBackbutton}
          showNextButton={this.showNextButton}
          backClick={() => this.onBackOrNext(false)}
          showHeader={false}
          showFooter={!this.loader.showLoader}
          showCancelButton={false}
          headerComponent={
            <Header
              headerCaption="CREATE YOUR ITEM TO SELL"
              showSaveButton={!this.showNextButton}
              saveAndExitButtonCaption={
                this.showNextButton ? "NEXT" : "SAVE & EXIT"
              }
              disableSaveButton={
                this.props.productStore?.isSaveUpdateInProgress ||
                !this.productId
              }
              saveClick={() => this.onSave()}
              cancelClick={() => this.onCancel()}
              cancelButtonCaption={this.headerCancelButtonCaption}
              saveAndExitClick={() => {
                if (this.loader.showLoader) return;
                this.showNextButton
                  ? this.onBackOrNext(this.showNextButton)
                  : this.onSave(true);
              }}
            />
          }
        >
          {this.loader.showLoader ? this.loader.loaderComponent : this.routes}
        </Layout>
      </Provider>
    );
  }
}

export default withRouter(ProductRoutes);
